import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useRouter } from 'next/router';
import axiosConfig from '../../../store/sagas/axiosConfig';
import { SearchStatus, PageLinks } from '../../../constants';
import {
  loginSuccess,
  logout,
  setLoaded,
  setAuthError,
  getTravelAgencySettingsRequest,
} from '../../../store/slices/user';

const Auth0ProvidedElement = ({ children }) => {
  const { user, getAccessTokenSilently, isLoading, isAuthenticated, error } = useAuth0();

  const router = useRouter();
  const { travelAgencyAliasName } = router.query;
  const { travelAgencySearchStatus } = useSelector((store) => store.user);

  const dispatch = useDispatch();

  useEffect(() => {
    if (travelAgencySearchStatus === SearchStatus.INITIAL) {
      dispatch(getTravelAgencySettingsRequest({ isLoading }));
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const handledAuthErrorRef = useRef(null);
  useEffect(() => {
    if (!error || handledAuthErrorRef.current === error) {
      return;
    }
    handledAuthErrorRef.current = error;

    // only failed sign-in attempts are worth surfacing: they carry an OAuth code (error.error)
    // AND the ?error= param auth0-react leaves in the URL (it cleans the URL only on success).
    // Anything else — background checkSession timeouts, already-consumed callback state — is not
    // a sign-in attempt and must not trigger the banner or a redirect.
    const isLoginRedirectError = /[?&]error=/.test(window.location.search);
    if (!error.error || !isLoginRedirectError) {
      return;
    }

    dispatch(
      setAuthError({
        code: error.error,
        description: error.error_description || error.message,
      }),
    );

    // redirect_uri is the site origin, so failed logins land on pages that never show the banner
    if (router.pathname !== PageLinks.Login.url) {
      router.push(PageLinks.Login.url);
    }
  }, [dispatch, error, router]);

  useEffect(() => {
    (async () => {
      try {
        if (isLoading || travelAgencySearchStatus === SearchStatus.SEARCHING) {
          return;
        }

        if (isAuthenticated) {
          const token = await getAccessTokenSilently();
          axiosConfig.defaults.headers.Authorization = `Bearer ${token}`;
          axiosConfig.defaults.params = axiosConfig.defaults.params || {};
          dispatch(
            loginSuccess({
              token,
              email: user.email,
              defaultCity: user.defaultCity,
              name: user.name,
              user,
            }),
          );
        } else {
          dispatch(logout());
          if (travelAgencyAliasName) {
            axiosConfig.defaults.params = {
              ...axiosConfig.defaults.params,
              travelAgencyAliasName,
            };
          }
        }
      } catch {
        dispatch(setLoaded());
      }
    })();
  }, [
    dispatch,
    user,
    isLoading,
    getAccessTokenSilently,
    isAuthenticated,
    travelAgencySearchStatus,
    travelAgencyAliasName,
  ]);

  return children || <></>;
};

Auth0ProvidedElement.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Auth0ProvidedElement;
